/**
 * Right-pane content for the unified Tests page when a test is
 * selected (#542).
 *
 * Replaces the entire right column of the old TestPlan.tsx,
 * preserving every existing capability AND adding two new toggles:
 *
 *   - **View toggle** (Test Case / Test Script / Side-by-side) —
 *     operator decides whether to scan the Playwright code, the
 *     English plan, or both at once. Default is Test Script (the
 *     code is what executes and what fails). Side-by-side splits
 *     the body via `<ResizablePanels>`; the other modes render a
 *     single scrollable column. Internal `ViewMode` values stay
 *     `'side-by-side' | 'english' | 'script'` (the persisted
 *     localStorage key uses these); only the visible labels and
 *     the button order changed.
 *   - **Read / Edit toggle** — Read renders description / steps /
 *     expected as text (and Monaco read-only); Edit swaps in
 *     `<Textarea>`s for those + Monaco editable + a Save button.
 *
 * **Preserved from TestPlan parity:**
 *   - Inline selectors for priority (P0–P3), intent, role (sourced
 *     from variant credentials); these save immediately on change.
 *   - Multi-suite chips on the header (new in unified) — also
 *     immediate save per toggle.
 *   - Tags editor (add via text input, remove via × per chip).
 *   - Fixture badges + source-library tag + workflow text.
 *   - Fixture relationships: depends-on for regular tests,
 *     used-by for fixtures, with cross-links that navigate the
 *     sidebar selection.
 *   - Page-snapshots accordion (per-step DOM dumps).
 *   - Blocked-reason callout.
 *   - Delete button with confirmation dialog.
 *
 * **Save semantics:**
 *   - Inline selectors (priority/intent/role/tags/suite-membership):
 *     immediate save per change. Matches TestPlan's existing pattern.
 *   - Edit-mode body fields (description/steps/expected/scriptCode):
 *     buffered into a draft state, persisted on Save button click.
 *     This avoids per-keystroke API spam and gives the operator a
 *     clear "I've changed something — commit?" moment.
 *
 * The pane reloads its parent's data via `onReload()` after every
 * successful mutation so the sidebar's health-icon / status pill
 * stay in sync.
 */
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PanelRight, Pencil, Play, Wrench } from 'lucide-react'
import {
  Camera,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  History,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { CopyIconButton } from '@/components/CopyIconButton'
import { formatDateTime } from '@/lib/utils'
import { PrioritySelect } from '@/components/PrioritySelect'
import { IntentSelect } from '@/components/IntentSelect'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/ui'
import { ResizablePanels } from '@/components/ResizablePanels'
import { useAppContext } from '@/hooks/useAppContext'
import { useIdentityReady } from '@/hooks/useIdentity'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import { applicationsAPI, testCasesAPI, testDataAPI, testPlansAPI, type TestCaseRevision } from '@/lib/api'
import { useToast } from '@/hooks/useToast'
import { useManualRunTemplates } from '@/hooks/useManualRunTemplates'
import { useStartRun } from '../useStartRun'
import { describePhases, phaseLabel } from '@/lib/runPhases'
import type { McpStep, PlanTestCase, TestCase, TestSuite } from '@/types'
import { buildTestCaseSaveBody } from './testCaseSaveBody'
import { renderAssertion } from '@/types'
import { MonacoCodePane } from './MonacoCodePane'
import { MultiSuiteChips } from './MultiSuiteChips'
import { TestDataBanner } from './TestDataBanner'
import { SaveCommitDialog } from './SaveCommitDialog'
import { HistoryPanel } from './HistoryPanel'
import { DiffPane } from './DiffPane'
import { RestoreCommitDialog } from './RestoreCommitDialog'
import { McpStepsEditor, mcpStepsEqual } from './McpStepsEditor'
import type { TestCaseHistoryEntry } from '@/lib/api'

type ViewMode = 'side-by-side' | 'english' | 'script'
type EditMode = 'read' | 'edit'


/** One label/value row in the Details drawer (#2257): the field name on
 * the left, its value or control on the right. */
function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 py-2 border-b border-dashed border-border/60">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
      <div className="min-w-0 text-right text-xs">{children}</div>
    </div>
  )
}

export interface TestDetailProps {
  testCase: PlanTestCase
  merged: TestCase | undefined
  allCases: PlanTestCase[]
  allSuites: TestSuite[]
  onReload: () => void | Promise<void>
  /** Sidebar selection callback so depends-on / used-by chips can
   * cross-navigate without leaving the page. */
  onSelectTest: (id: string) => void
  /** Every suggestion of the app, archived ones included, by id. Drives
   * the Suggestion chip. `undefined` while unknown: the chip then stays
   * hidden rather than guessing whether this test is linked. */
  suggestionsById?: ReadonlyMap<string, { name: string; active: boolean }>
  /** Open a suggestion on the Proposed tab. */
  onOpenSuggestion?: (candidateId: string) => void
}

/** What "Add as a suggestion" reused, said on screen. A new suggestion
 * needs no explanation; reusing one with the same text does, and an
 * archived one needs a way to restore it. */
type SuggestionNotice =
  | { kind: 'existing'; name: string }
  | { kind: 'archived'; id: string; name: string }

export function TestDetail({
  testCase,
  merged,
  allCases,
  allSuites,
  onReload,
  onSelectTest,
  suggestionsById,
  onOpenSuggestion,
}: TestDetailProps) {
  const navigate = useNavigate()
  const toast = useToast()
  const wp = useWorkspacePath()
  const { selectedAppId, selectedVariantId, selectedVariant } = useAppContext()
  const { user } = useIdentityReady()

  // View / Edit modes are operator-muscle-memory across page
  // reloads — localStorage, not URL. Default view = 'script' so the
  // first thing the operator sees is the Playwright code that's
  // going to run (the English plan is the source spec; the script
  // is what gets executed and what fails).
  const [viewMode, setViewMode] = usePersistedState<ViewMode>('tests-view-mode', 'script')
  const [editMode, setEditMode] = usePersistedState<EditMode>('tests-edit-mode', 'read')
  // Details drawer open/closed (#2257). Per-browser, same as the View
  // and Mode toggles above — a layout preference, not per-user state.
  const [detailsOpen, setDetailsOpen] = usePersistedState<boolean>('tests-details-open', false)

  // Edit-mode draft state — buffered until Save. Re-seeded any
  // time the selected case changes OR the underlying data
  // refreshes (matches TestPlan's draft reset behavior).
  //
  // After #596 the canonical step shape is `McpStep[]`; the editor
  // operates on it directly instead of a flat string-list draft.
  const [draftName, setDraftName] = useState(testCase.name)
  const [draftDescription, setDraftDescription] = useState(testCase.description ?? '')
  const [draftMcpSteps, setDraftMcpSteps] = useState<McpStep[]>(testCase.mcpSteps ?? [])
  const [draftScript, setDraftScript] = useState(merged?.scriptCode ?? '')
  useEffect(() => {
    setDraftName(testCase.name)
    setDraftDescription(testCase.description ?? '')
    setDraftMcpSteps(testCase.mcpSteps ?? [])
    setDraftScript(merged?.scriptCode ?? '')
  }, [testCase.id, testCase.name, testCase.description, testCase.mcpSteps, merged?.scriptCode])

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Is the buffered draft different from the canonical source? Save
  // is disabled when not — there's nothing to commit, and exposing a
  // live Save button on a clean form invites a dialog → empty commit.
  // Steps are deep-compared via `mcpStepsEqual` so per-step `expect[]`
  // re-orderings or empty-string differences surface as dirty (or not)
  // exactly as the renderer/editor would treat them.
  const dirty = useMemo(() => {
    return (
      draftName !== testCase.name ||
      draftDescription !== (testCase.description ?? '') ||
      draftScript !== (merged?.scriptCode ?? '') ||
      !mcpStepsEqual(draftMcpSteps, testCase.mcpSteps ?? [])
    )
  }, [draftName, draftDescription, draftMcpSteps, draftScript, testCase.name, testCase.description, testCase.mcpSteps, merged?.scriptCode])

  // Credential roles for the role-selector dropdown. Loaded once
  // per variant change. Quietly empty on failure — the dropdown
  // still works (you just get the bare "unauthenticated" option).
  const [availableRoles, setAvailableRoles] = useState<string[]>([])
  const [retrying, setRetrying] = useState(false)
  useEffect(() => {
    if (!selectedVariantId) {
      setAvailableRoles([])
      return
    }
    let cancelled = false
    testDataAPI
      .get(selectedVariantId)
      .then((doc: { credentials?: Array<{ role?: string }> }) => {
        if (cancelled) return
        setAvailableRoles((doc.credentials ?? []).map((c) => c.role).filter((r): r is string => Boolean(r)))
      })
      .catch(() => !cancelled && setAvailableRoles([]))
    return () => {
      cancelled = true
    }
  }, [selectedVariantId])

  // Tags editor — open/close toggle + the staging text for a new tag.
  const [editingTags, setEditingTags] = useState(false)
  const [newTag, setNewTag] = useState('')

  // Snapshots accordion (per-step DOM dumps). Top-level collapse
  // + nested per-step expansion.
  const [snapshotsExpanded, setSnapshotsExpanded] = useState(false)
  const [expandedSnapshotIndex, setExpandedSnapshotIndex] = useState<number | null>(null)

  // Delete confirmation modal.
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  // Save-with-commit-message dialog. Opened by the Save button in
  // Edit mode; the actual save runs inside the dialog's onSubmit so
  // the dialog can close on success.
  const [commitDialogOpen, setCommitDialogOpen] = useState(false)

  // Version-history side panel + selected revision. When historyOpen
  // is false the right area renders the live Monaco unchanged. When
  // historyOpen is true the side panel is visible; when a row is
  // selected, the script column is replaced with a DiffPane.
  const [historyOpen, setHistoryOpen] = useState(false)
  const [selectedHistorySha, setSelectedHistorySha] = useState<string | null>(null)
  // Restore confirmation. HistoryPanel hands us the row data + the
  // current commit's sha (so the dialog can detect plan divergence
  // via two parallel revision fetches without a HEAD-ref endpoint).
  /** Settings for a run this person starts and watches. `replay` is the
   * kind that runs tests that already exist, which is what this button
   * does. Absent when the variant has none, and then the button is not
   * offered at all. */
  const { byKind: manualTemplates } = useManualRunTemplates(selectedVariantId)
  const replayTemplateId = manualTemplates.replay?.id
  const resumeTemplateId = manualTemplates.resume?.id
  /** Which stages each button will run (#2225). Read from the template the
   * button uses, so an edited one tells the truth. Run and fix is the case
   * this exists for: it overrides the saved phases with execute + heal, so
   * it skips Plan and Generate and nothing said so. */
  const runThisTestStages = describePhases(manualTemplates.replay)
  /** Write this test must include the writing step, even when the saved
   * Resume Tests settings leave it out (a run-and-heal-only template would
   * otherwise "write" nothing). Null means the saved settings already
   * write, so nothing is overridden. */
  const writePhases = useMemo(() => {
    const saved = manualTemplates.resume?.phaseFilter ?? []
    if (saved.length === 0 || saved.includes('generate')) return null
    return ['generate', ...saved]
  }, [manualTemplates.resume])
  const writeThisTestStages = writePhases
    ? writePhases.map(p => phaseLabel(p as Parameters<typeof phaseLabel>[0])).join(' → ')
    : describePhases(manualTemplates.resume)
  const runAndFixStages = [phaseLabel('execute'), phaseLabel('heal')].join(' → ')
  const startRun = useStartRun()
  const startingRun = startRun.starting

  /** What "Run and fix" lays over the saved Resume Tests settings, for one
   * run only (#2194).
   *
   * Not a fourth saved template: it appears per test and nowhere else, so
   * there is nothing to edit and nothing to keep. The trace is always on
   * because the trace is what the fixer reads to work out what broke — a
   * cost on an ordinary run, the input here. The timeouts are double the
   * usual so a test that failed only because the app was slow is not
   * diagnosed as broken and "fixed" into something wrong. Console and
   * network errors are reported for the same reason: they are often the
   * actual cause.
   *
   * How many attempts a run makes is NOT set here. The healer caps itself
   * at MAX_HEAL_ATTEMPTS, so every caller inherits one limit rather than
   * competing with a second. */
  const RUN_AND_FIX_SETTINGS = {
    phaseFilter: ['execute', 'heal'],
    playwrightTimeout: 60_000,
    assertionTimeout: 15_000,
    captureTrace: 'always',
    logLevel: 'debug',
    testDataMode: 'off',
    reportConsoleErrors: true,
    reportNetworkErrors: true,
  } as const

  /** Run this one test.
   *
   * The saved settings are read and this test's id is sent as a one-run
   * override, so what Run Tests does next time is unchanged (#2175 slice 2
   * gave the trigger that ability, and the server refuses an override on
   * anything but the manual pool). Nothing here writes.
   *
   * Stays on this page and offers the run, matching "Create tests" on the
   * Proposed tab. Navigating away would cost the operator their place in a
   * list they are probably working down. */
  async function handleRunThisTest() {
    if (!replayTemplateId || startingRun) return
    void startScopedRun(replayTemplateId, `Running ${testCase.name}…`)
  }

  /** Run it, then try to fix it if it fails (#2194). */
  async function handleRunAndFix() {
    if (!resumeTemplateId || startingRun) return
    void startScopedRun(resumeTemplateId, `Fixing ${testCase.name}…`, RUN_AND_FIX_SETTINGS)
  }

  /** Write the test that has not been written yet (#2181 slice 4).
   *
   * Same shape as running one: the saved Resume Tests settings with this
   * test's id sent for one run. The scope is what makes the button mean
   * the test on screen — a resume otherwise takes the oldest unfinished
   * case within its cap, so without it this would write a different test
   * and nothing would say so. */
  async function handleWriteThisTest() {
    if (!resumeTemplateId || startingRun) return
    void startScopedRun(resumeTemplateId, `Writing ${testCase.name}…`, writePhases ? { phaseFilter: writePhases } : {})
  }

  /** Start a run for just this test. The hook owns triggering, saying so
   * and going to the run — shared with New Test on the page above, which
   * wants exactly the same three things (#2194). */
  async function startScopedRun(
    templateId: string,
    startedMessage: string,
    extraSettings: Record<string, unknown> = {},
  ) {
    await startRun.start(templateId, startedMessage, {
      ...extraSettings,
      // LAST, so it cannot be overridden. This helper means "run THIS
      // test"; a caller that could widen the selection would start a run
      // against tests the operator did not press the button on, and the
      // run would look entirely normal (Copilot review).
      testCaseIds: [testCase.id],
    })
  }

  const [restoreTarget, setRestoreTarget] = useState<
    { revision: TestCaseHistoryEntry; currentSha: string } | null
  >(null)
  const [restoring, setRestoring] = useState(false)
  // Bumped after a successful restore so HistoryPanel's `key` changes,
  // forcing a remount + refetch. Without this the panel keeps its
  // stale `entries`, the old HEAD still reads as "current" + restore
  // remains offered on it, until the operator closes and reopens the
  // panel. (Copilot review on the restore PR.)
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0)
  type RevisionState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; revision: TestCaseRevision }
    | { kind: 'missing' }
    | { kind: 'error'; message: string }
  const [revisionState, setRevisionState] = useState<RevisionState>({ kind: 'idle' })

  // "Add as a suggestion" (and the Restore its notice may offer).
  const [addingSuggestion, setAddingSuggestion] = useState(false)
  const [suggestionNotice, setSuggestionNotice] = useState<SuggestionNotice | null>(null)
  useEffect(() => { setSuggestionNotice(null) }, [testCase.id])

  /** Turn this test into a suggestion so it counts toward coverage. The
   * server reuses a suggestion with exactly the same text instead of
   * making a second one, and says so; the page's reload then refreshes
   * the counts, the chip and the Proposed tab together. */
  async function addAsSuggestion() {
    if (!selectedAppId || addingSuggestion) return
    setAddingSuggestion(true)
    setSaveError(null)
    try {
      const res = await applicationsAPI.addSuggestionFromTest(selectedAppId, testCase.id)
      if (res.outcome === 'existing') {
        setSuggestionNotice({ kind: 'existing', name: res.candidate.name })
      } else if (res.outcome === 'archived') {
        setSuggestionNotice({ kind: 'archived', id: res.candidate._id, name: res.candidate.name })
      } else {
        toast({ title: 'Added as a proposed test', variant: 'success' })
      }
      await onReload()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Couldn't add the proposed test")
    } finally {
      setAddingSuggestion(false)
    }
  }

  async function restoreSuggestion(id: string) {
    if (!selectedAppId) return
    setSaveError(null)
    try {
      await applicationsAPI.updateCandidate(selectedAppId, id, { active: true })
      setSuggestionNotice(null)
      await onReload()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Restore failed')
    }
  }

  // Clear history state whenever the selected test case changes —
  // the previous test's revisions are meaningless for the new one.
  useEffect(() => {
    setHistoryOpen(false)
    setSelectedHistorySha(null)
    setRevisionState({ kind: 'idle' })
    setRestoreTarget(null)
  }, [testCase.id])

  async function doRestore(commit: { message: string; details?: string }) {
    if (!restoreTarget) return
    setRestoring(true)
    setSaveError(null)
    try {
      await testCasesAPI.restore(testCase.id, restoreTarget.revision.fullSha, commit)
      setRestoreTarget(null)
      // After restore, drop the diff view back to live Monaco — the
      // newest commit is now the restore commit itself, and the user
      // wants to see what landed, not compare to what they just
      // overwrote.
      setSelectedHistorySha(null)
      setRevisionState({ kind: 'idle' })
      // Force HistoryPanel to remount + refetch so the new restore
      // commit shows as current and the "previous HEAD" is no longer
      // marked current / offered for restore on stale data.
      setHistoryRefreshKey(k => k + 1)
      await onReload()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Restore failed')
    } finally {
      setRestoring(false)
    }
  }

  // Lazy-fetch the revision content when the user picks a row in the
  // history panel. Tri-state load (loading/ready/missing/error) so
  // the script-pane render can tell a 404 (`missing`) apart from an
  // in-flight request, and so a failed fetch never leaves stale
  // ready-content visible under an error banner.
  useEffect(() => {
    if (!selectedHistorySha) {
      setRevisionState({ kind: 'idle' })
      return
    }
    let cancelled = false
    setRevisionState({ kind: 'loading' })
    testCasesAPI
      .getRevision(testCase.id, selectedHistorySha)
      .then((data) => {
        if (cancelled) return
        setRevisionState(data ? { kind: 'ready', revision: data } : { kind: 'missing' })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setRevisionState({
          kind: 'error',
          message: e instanceof Error ? e.message : 'Failed to load revision',
        })
      })
    return () => {
      cancelled = true
    }
  }, [selectedHistorySha, testCase.id])

  const isFixture = testCase.type === 'fixture'
  const isBlocked = testCase.status === 'blocked'
  const scriptCode = merged?.scriptCode ?? ''
  /** Run this test is offered only when there is a script AND settings to
   * run it with. A test that was never written, or whose write was blocked,
   * has nothing to run. */
  const lastRunFailed = merged?.healthStatus === 'failing'
  /** The steps were changed after the script was written, so the script
   * checks the old steps and the next run that writes tests replaces it.
   * Running or fixing the old script would only repeat its old checks, so
   * Write this test is offered instead. */
  const rewriteRequested = testCase.status === 'planned' && !!scriptCode && !isFixture
  /** Run this test is for a script that is not known to be failing. A
   * failing or blocked one gets Run and fix instead, so exactly one button
   * sits beside the name. */
  const canRunThisTest = !!replayTemplateId && !!scriptCode && !isBlocked && !lastRunFailed && !rewriteRequested
  /** Run and fix takes the other half: a script that failed, or one the
   * writer gave up on. Blocked had no button at all before (#2194). */
  const canRunAndFix = !!resumeTemplateId && !!scriptCode && (isBlocked || lastRunFailed) && !rewriteRequested
  /** How many fixes have already been tried, so the operator can see that
   * the last two failed before starting a third. Never a limit. */
  const healAttempts = merged?.healAttempts ?? 0
  /** Write this test takes the other half: a test with no script yet, and
   * settings to write it with (#2181 slice 4). The two are exclusive, so
   * exactly one button occupies that spot. */
  const canWriteThisTest = !!resumeTemplateId && !isBlocked && (!scriptCode || rewriteRequested)

  // Fixture relationships — symmetric. Regular tests show
  // depends-on; fixtures show used-by.
  const dependencyCases = useMemo(() => {
    if (isFixture) return []
    return (testCase.dependencies ?? [])
      .map((id) => ({ id, c: allCases.find((c) => c.id === id) }))
  }, [testCase.dependencies, allCases, isFixture])

  const dependentCases = useMemo(() => {
    if (!isFixture) return []
    return allCases.filter((c) => c.dependencies?.includes(testCase.id))
  }, [allCases, testCase.id, isFixture])

  // ── Mutations ────────────────────────────────────────────────────

  async function updateInline(patch: Partial<PlanTestCase>) {
    if (!selectedAppId || !selectedVariantId) return
    try {
      await testPlansAPI.updateTestCase(selectedAppId, selectedVariantId, testCase.id, patch)
      await onReload()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed')
    }
  }

  // #1978 — re-check a sign-in-blocked test now. Clears the block if the
  // sign-in resolves, refreshes the reason if it still can't. Same reload
  // path as the inline edits.
  async function doRetry() {
    if (!selectedAppId || !selectedVariantId) return
    setRetrying(true)
    setSaveError(null)
    try {
      await testCasesAPI.retry(testCase.id)
      await onReload()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Try again failed')
    } finally {
      setRetrying(false)
    }
  }

  async function saveBodyEdits(commit: { message: string; details?: string }) {
    setSaving(true)
    setSaveError(null)
    try {
      // Body edits target two endpoints:
      //   - testCasesAPI.update: name, description, mcpSteps,
      //     scriptCode. Atomic per-app git commit lands here.
      //   - testPlansAPI.updateTestCase: priority/intent/role/
      //     categories (handled separately by the inline selectors).
      await testCasesAPI.update(
        testCase.id,
        buildTestCaseSaveBody({
          name: draftName,
          description: draftDescription,
          mcpSteps: draftMcpSteps,
          draftScript,
          // Sent only when a script row exists and its text was changed:
          // the server 404s on scriptCode with no row (#2251), and treats a
          // save without it as a step edit that asks for a new script.
          savedScript: merged?.scriptId ? (merged.scriptCode ?? '') : null,
        }),
        commit,
      )
      setEditMode('read')
      setCommitDialogOpen(false)
      await onReload()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function doDelete() {
    if (!selectedAppId || !selectedVariantId) return
    try {
      await testPlansAPI.deleteTestCase(selectedAppId, selectedVariantId, testCase.id)
      setConfirmingDelete(false)
      await onReload()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Delete failed')
      setConfirmingDelete(false)
    }
  }

  function addTag(tag: string) {
    const trimmed = tag.trim()
    if (!trimmed) return
    const current = testCase.categories ?? []
    // Strict union (PlanCategoryName) is bypassed because tags are
    // free-form operator strings — the backend accepts any string;
    // the type's predefined union is the planner's suggestion set,
    // not a hard schema constraint. Mirrors TestPlan.tsx:258's
    // `as any` / `as string[]` pattern.
    if (current.includes(trimmed as PlanTestCase['categories'][number])) return
    void updateInline({ categories: [...current, trimmed] as PlanTestCase['categories'] })
    setNewTag('')
  }

  function removeTag(tag: string) {
    const current = testCase.categories ?? []
    void updateInline({ categories: current.filter((c) => c !== tag) as PlanTestCase['categories'] })
  }

  /** Render the test case as plain text for the English-view copy
   * affordance (#616) — name, description, then numbered steps with
   * their per-step expects nested underneath. Format matches what
   * an operator would paste into a doc / ticket without needing to
   * re-layout. */
  function formatPlanAsText(): string {
    const lines: string[] = []
    lines.push(testCase.name)
    if (testCase.description?.trim()) {
      lines.push('')
      lines.push(`Description: ${testCase.description.trim()}`)
    }
    const steps = testCase.mcpSteps ?? []
    if (steps.length > 0) {
      lines.push('')
      lines.push('Steps:')
      steps.forEach((step, i) => {
        const action = step.perform?.trim() || '(no action)'
        lines.push(`${i + 1}. ${action}`)
        for (const exp of step.expect) {
          lines.push(`   → Expect: ${renderAssertion(exp)}`)
        }
      })
    }
    return lines.join('\n')
  }

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col bg-card">
      {/* Row 1 — the workflow this test lives in, its name, and the
          actions reached for most (#2257). Name leads; the View/Mode
          toggles that used to sit above it moved down to row 2, and the
          informational fields moved into the Details drawer. */}
      <div className="flex-none px-5 py-2.5 border-b">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {editMode === 'edit' ? (
              <Input value={draftName} onChange={(e) => setDraftName(e.target.value)} className="text-lg font-semibold" />
            ) : (
              <h2 className="text-lg font-semibold truncate" title={testCase.name}>{testCase.name}</h2>
            )}
          </div>
          <div className="flex items-start gap-2 shrink-0">
            {/* Run just this one (#2181 slice 2). Only when there is a
                script to run: on a planned test the button would do
                nothing, so the script pane says so instead. */}
            {canRunThisTest && (
              <span title="Run just this test, not the whole suite." className="shrink-0 text-center">
                <Button size="sm" disabled={startingRun} onClick={() => void handleRunThisTest()}>
                  <Play className="h-3.5 w-3.5 mr-1.5" />
                  {startingRun ? 'Starting…' : 'Run this Test'}
                </Button>
                {runThisTestStages && (
                  <p className="mt-1 text-[10px] text-muted-foreground">{runThisTestStages}</p>
                )}
              </span>
            )}
            {canRunAndFix && (
              <span title="Run this test and try to fix it if it fails." className="shrink-0 text-center">
                <Button size="sm" disabled={startingRun} onClick={() => void handleRunAndFix()}>
                  <Wrench className="h-3.5 w-3.5 mr-1.5" />
                  {startingRun ? 'Starting…' : 'Run and Fix'}
                </Button>
                <p className="mt-1 text-[10px] text-muted-foreground">{runAndFixStages}</p>
                {healAttempts > 0 && (
                  <p className="mt-1 text-[10px] text-amber-600 dark:text-amber-500">
                    {healAttempts === 1
                      ? 'Attempted fix once before'
                      : healAttempts === 2
                        ? 'Attempted fix twice before'
                        : `Attempted fix ${healAttempts} times before`}
                  </p>
                )}
              </span>
            )}
            {canWriteThisTest && (
              <span title="Write this test so it can be run." className="shrink-0 text-center">
                <Button size="sm" disabled={startingRun} onClick={() => void handleWriteThisTest()}>
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  {startingRun ? 'Starting…' : 'Write this Test'}
                </Button>
                {writeThisTestStages && (
                  <p className="mt-1 text-[10px] text-muted-foreground">{writeThisTestStages}</p>
                )}
              </span>
            )}
            {editMode === 'edit' && (
              // Wrapper span owns the tooltip: a disabled button gets
              // pointer-events-none and never receives hover.
              <span title={!saving && !dirty ? 'No changes to save' : undefined}>
                <Button size="sm" onClick={() => setCommitDialogOpen(true)} disabled={saving || !dirty}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </span>
            )}
            {/* Secondary actions as icons that name themselves on hover
                (#2265). Delete is a direct red icon, not a kebab — the whole
                point is to surface it. History and Details keep their active
                state via the button variant. */}
            <Button
              variant={historyOpen ? 'default' : 'ghost'}
              size="icon-sm"
              onClick={() => {
                setHistoryOpen((open) => !open)
                if (historyOpen) setSelectedHistorySha(null)
              }}
              title="History"
              aria-label="History"
              aria-pressed={historyOpen}
            >
              <History className="h-4 w-4" />
            </Button>
            <Button
              variant={detailsOpen ? 'default' : 'ghost'}
              size="icon-sm"
              onClick={() => setDetailsOpen((open) => !open)}
              title="Details"
              aria-label="Details"
              aria-pressed={detailsOpen}
            >
              <PanelRight className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setConfirmingDelete(true)}
              title="Delete this Test"
              aria-label="Delete this Test"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <SaveCommitDialog
        open={commitDialogOpen}
        onOpenChange={(open) => !saving && setCommitDialogOpen(open)}
        author={{ name: user.name || user.email, email: user.email }}
        onSubmit={saveBodyEdits}
        saving={saving}
      />

      {saveError && (
        <div className="flex-none border-b bg-destructive/10 text-destructive text-xs px-5 py-2 flex items-start gap-3" role="alert">
          <span className="flex-1"><strong>Error:</strong> {saveError}</span>
          <button type="button" className="underline" onClick={() => setSaveError(null)}>Dismiss</button>
        </div>
      )}

      {suggestionNotice && (
        <div className="flex-none border-b bg-muted/40 text-xs px-5 py-2 flex items-center gap-3" role="status">
          <span className="flex-1">
            {suggestionNotice.kind === 'existing'
              ? `Linked to the existing proposed test with the same text: ${suggestionNotice.name}.`
              : `Linked to an archived proposed test with the same text: ${suggestionNotice.name}. Restore it to count this test.`}
          </span>
          {suggestionNotice.kind === 'archived' && (
            <Button size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => void restoreSuggestion(suggestionNotice.id)}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Restore
            </Button>
          )}
          <button type="button" className="underline" onClick={() => setSuggestionNotice(null)}>Dismiss</button>
        </div>
      )}

      {/* Row 2 — how you view the body (View / Mode) and what the test is
          attached to (Test Data, Suites), split by a divider (#2257). The
          informational fields moved into the Details drawer, opened from
          row 1. */}
      {/* Single non-wrapping strip: on a narrow pane it scrolls sideways
          rather than wrapping to a third line, so the header stays two rows
          (#2257 Copilot review). `[&>*]:shrink-0` keeps each control at its
          natural width; the Suites popover is portaled, so the horizontal
          scroll container can't clip it. */}
      <div className="flex-none px-5 py-2 border-b flex items-center gap-2.5 flex-nowrap overflow-x-auto text-xs [&>*]:shrink-0">
        <span className="text-muted-foreground">View</span>
        <div className="inline-flex bg-muted rounded-md overflow-hidden">
          {(['english', 'script', 'side-by-side'] as ViewMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setViewMode(m)}
              className={`px-2.5 py-1 ${viewMode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {m === 'script' ? 'Test Script' : m === 'english' ? 'Test Case' : 'Side-by-side'}
            </button>
          ))}
        </div>
        <span className="text-muted-foreground">Mode</span>
        <div className="inline-flex bg-muted rounded-md overflow-hidden">
          {(['read', 'edit'] as EditMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setEditMode(m)}
              className={`px-2.5 py-1 ${editMode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {m === 'read' ? 'Read' : 'Edit'}
            </button>
          ))}
        </div>
        {/* Divider: body-view controls on the left, the test's
            associations on the right. */}
        <span className="self-stretch w-px bg-border mx-1" />
        <TestDataBanner scriptId={merged?.scriptId} testCaseId={testCase.id} compact />
        <MultiSuiteChips
          allSuites={allSuites}
          testCaseId={testCase.id}
          onChanged={onReload}
          onError={setSaveError}
          compact
        />
        {/* Which suggestion this test counts toward. Coverage is counted per
            suggestion, so a test with none (or one whose suggestion was
            deleted) runs but counts toward nothing; this is where that
            shows and where it is fixed. Sign-in helpers never count, so
            they get no chip. Hidden until the suggestion list is known. */}
        {!isFixture && suggestionsById && (() => {
          const id = testCase.candidateTestId
          const linked = id ? suggestionsById.get(id) : undefined
          if (id && linked) {
            return (
              <button
                type="button"
                onClick={() => onOpenSuggestion?.(id)}
                className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted whitespace-nowrap"
                title={linked.active
                  ? `Open "${linked.name}" on the Proposed tab`
                  : `"${linked.name}" is archived, so this test doesn't count toward coverage. Open it to restore it.`}
              >
                <span className="text-muted-foreground font-normal">Proposed test</span>
                <span className="text-muted-foreground">·</span>
                <span className="truncate max-w-[14rem]">{linked.name}</span>
                {!linked.active && <span className="text-muted-foreground font-normal">(archived)</span>}
                <ChevronRight className="h-3 w-3 text-primary shrink-0" />
              </button>
            )
          }
          return (
            <span
              className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium whitespace-nowrap"
              title="Not linked to a proposed test, so it doesn't count toward coverage."
            >
              <span className="text-muted-foreground font-normal">Proposed test</span>
              <span className="text-muted-foreground">·</span>
              <span>None</span>
              <Button size="sm" className="h-6 px-2 text-xs" disabled={addingSuggestion}
                onClick={() => void addAsSuggestion()}>
                {addingSuggestion ? 'Adding…' : 'Add as a proposed test'}
              </Button>
            </span>
          )
        })()}
      </div>

      {/* Body — view-mode dispatch, optionally with the version-history
          side panel on the right, and the Details drawer overlaying the
          right edge on demand (#2257). `relative` anchors that overlay. */}
      <div className="flex-1 min-h-0 overflow-hidden flex relative">
        <div className="flex-1 min-w-0">
          {viewMode === 'side-by-side' ? (
            <ResizablePanels
              // Nested split inside the page's right column. 480px
              // gives the English pane comfortable room for the
              // description + steps prose while leaving Monaco enough
              // width to render Playwright code without aggressive
              // word-wrap. Operator can drag to taste; min/max keeps
              // either pane usable. PR #543 Copilot review fix —
              // previously passed `undefined` via a cast, which only
              // worked accidentally via JS default-param fallthrough.
              defaultWidth={480}
              minWidth={280}
              maxWidth={900}
              left={renderEnglishPane()}
              right={renderScriptPane()}
            />
          ) : viewMode === 'english' ? (
            <div className="h-full overflow-y-auto">{renderEnglishPane()}</div>
          ) : (
            <div className="h-full">{renderScriptPane()}</div>
          )}
        </div>
        {historyOpen && (
          <HistoryPanel
            key={historyRefreshKey}
            testCaseId={testCase.id}
            selectedSha={selectedHistorySha}
            onSelect={setSelectedHistorySha}
            onClose={() => {
              setHistoryOpen(false)
              setSelectedHistorySha(null)
            }}
            onRestore={(revision, currentSha) => setRestoreTarget({ revision, currentSha })}
          />
        )}
        {/* Details drawer (#2257) — the informational fields, on demand.
            Overlays the body's right edge so rows 1–2 and the body do not
            reflow when it opens. Priority / Intent / Auth / Categories are
            still editable here, just as they were on the old header. */}
        {detailsOpen && (
          <div className="absolute top-0 right-0 bottom-0 w-[264px] border-l bg-card shadow-[-16px_0_34px_rgba(15,23,42,0.13)] z-20 overflow-y-auto">
            {/* No heading or Hide here (#2265): the Details button in the
                header toggles this drawer open and shut, so the panel opens
                straight onto the first field. */}
            <div className="p-3">
              <DetailRow label="Status">
                <Badge variant={testCase.status === 'scripted' ? 'default' : 'secondary'}>{testCase.status}</Badge>
              </DetailRow>
              <DetailRow label="Priority">
                <PrioritySelect
                  value={testCase.priority}
                  onChange={(p) => void updateInline({ priority: (p ?? undefined) as PlanTestCase['priority'] })}
                />
              </DetailRow>
              <DetailRow label="Intent">
                <IntentSelect
                  value={testCase.intent}
                  onChange={(i) => void updateInline({ intent: (i ?? undefined) as PlanTestCase['intent'] })}
                />
              </DetailRow>
              <DetailRow label="Auth">
                <select
                  value={testCase.role ?? ''}
                  onChange={(e) => void updateInline({ role: e.target.value || undefined })}
                  className="px-2 py-0.5 text-xs rounded cursor-pointer border border-border bg-background max-w-[9rem]"
                  title="Credential role this test runs as"
                >
                  <option value="">unauthenticated</option>
                  {availableRoles.map((r) => <option key={r} value={r}>{r}</option>)}
                  {testCase.role && !availableRoles.includes(testCase.role) && (
                    <option value={testCase.role}>{testCase.role} (not in credentials)</option>
                  )}
                </select>
              </DetailRow>
              {/* Categories (was labeled "Tags") — the inferred `categories`,
                  still editable inline. */}
              <div className="py-2 border-b border-dashed border-border/60">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Categories</span>
                  {!editingTags ? (
                    <button onClick={() => setEditingTags(true)} className="text-xs text-primary hover:underline">Edit</button>
                  ) : (
                    <button onClick={() => { setEditingTags(false); setNewTag('') }} className="text-xs text-muted-foreground hover:underline">Done</button>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {(testCase.categories ?? []).map((cat) => (
                    <Badge key={cat} variant="secondary" className="text-xs">
                      {cat}
                      {editingTags && (
                        <button onClick={() => removeTag(cat)} className="ml-1 hover:text-destructive" aria-label={`Remove category ${cat}`}>
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                  {editingTags && (
                    <div className="flex items-center gap-1">
                      <Input
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addTag(newTag)}
                        placeholder="Add…"
                        className="h-6 w-24 text-xs"
                      />
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => addTag(newTag)}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                  {!editingTags && (testCase.categories ?? []).length === 0 && (
                    <span className="text-xs text-muted-foreground italic">none</span>
                  )}
                </div>
              </div>
              {/* Fixture badge + provenance — only when they apply. */}
              {(isFixture || testCase.sourceLibraryId || testCase.signInHelperUpgradeAvailable !== undefined) && (
                <div className="py-2 border-b border-dashed border-border/60 flex flex-wrap gap-1.5">
                  {isFixture && (
                    <Badge variant="outline" className="text-amber-500 border-amber-500/30">fixture</Badge>
                  )}
                  {testCase.sourceLibraryId && (
                    <span className="px-2 py-0.5 text-xs rounded bg-muted text-muted-foreground border" title="Originally seeded from a SystemFixture library entry. May have diverged through edits / heals.">
                      from {testCase.sourceLibraryId}
                      {testCase.sourceLibraryVersion !== undefined && ` v${testCase.sourceLibraryVersion}`}
                    </span>
                  )}
                  {testCase.signInHelperUpgradeAvailable !== undefined && (
                    <span
                      className="px-2 py-0.5 text-xs rounded font-medium inline-flex items-center gap-1 bg-amber-500/10 text-amber-600 border border-amber-500/30"
                      title={`This sign-in helper was changed after it was set up (through edits or healing), so we kept it instead of replacing it. A newer version (v${testCase.signInHelperUpgradeAvailable}) is available.`}
                    >
                      ↑ Newer version available (v{testCase.signInHelperUpgradeAvailable})
                    </span>
                  )}
                </div>
              )}
              {/* Fixture relationships. */}
              {!isFixture && dependencyCases.length > 0 && (
                <div className="py-2 border-b border-dashed border-border/60 text-xs">
                  <span className="text-muted-foreground">Depends on: </span>
                  {dependencyCases.map(({ id, c }, i) => (
                    <span key={id}>
                      {i > 0 && ', '}
                      {c ? (
                        <button onClick={() => onSelectTest(id)} className="text-primary hover:underline">{c.name}</button>
                      ) : (
                        <span className="text-destructive italic" title="Fixture not found — may have been deleted">{id.slice(0, 8)}… (missing)</span>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {isFixture && (
                <div className="py-2 border-b border-dashed border-border/60 text-xs">
                  {dependentCases.length === 0 ? (
                    <span className="text-muted-foreground italic">Used by: 0 tests (no dependents — this fixture won't be triggered)</span>
                  ) : (
                    <>
                      <span className="text-muted-foreground">Used by {dependentCases.length} test{dependentCases.length !== 1 ? 's' : ''}: </span>
                      {dependentCases.map((d, i) => (
                        <span key={d.id}>
                          {i > 0 && ', '}
                          <button onClick={() => onSelectTest(d.id)} className="text-primary hover:underline">{d.name}</button>
                        </span>
                      ))}
                    </>
                  )}
                </div>
              )}
              {/* Dates — least-glanced, so they sit at the foot. #2160 keeps
                  both in full here because this is where one test is
                  examined; "changed" is hidden when it equals creation. */}
              {testCase.createdAt && (
                <p className="pt-2 text-[11px] text-muted-foreground">
                  <span title={formatDateTime(testCase.createdAt, 'absoluteUTC')}>
                    Created {formatDateTime(testCase.createdAt, 'relative')}
                  </span>
                  {testCase.updatedAt
                    && new Date(testCase.updatedAt).getTime() - new Date(testCase.createdAt).getTime() > 1000 && (
                    <>
                      {' · '}
                      <span title={formatDateTime(testCase.updatedAt, 'absoluteUTC')}>
                        changed {formatDateTime(testCase.updatedAt, 'relative')}
                      </span>
                    </>
                  )}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {restoreTarget && (
        <RestoreCommitDialog
          open={restoreTarget != null}
          onOpenChange={(open) => !restoring && !open && setRestoreTarget(null)}
          testCaseId={testCase.id}
          revision={restoreTarget.revision}
          currentSha={restoreTarget.currentSha}
          author={{ name: user.name || user.email, email: user.email }}
          onSubmit={doRestore}
          restoring={restoring}
        />
      )}

      {/* Delete confirmation */}
      <Dialog open={confirmingDelete} onOpenChange={(open) => !open && setConfirmingDelete(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete test?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-muted-foreground">
              Permanently delete <strong>"{testCase.name}"</strong> and its script? This cannot be undone.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmingDelete(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )

  function renderEnglishPane() {
    return (
      <div className="h-full flex flex-col">
        {/* Pane header mirrors the Script pane's "Playwright spec" bar —
            keeps the copy affordance consistent across both views (#616).
            Plain-text plan copies as name + description + numbered steps
            with per-step expects nested underneath; matches what an
            operator would paste into a doc / ticket. */}
        <div className="flex-none px-3 py-1 border-b flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          <span>Test plan</span>
          {editMode === 'read' && (
            <CopyIconButton value={formatPlanAsText()} title="Copy test plan" />
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Description */}
        <div>
          <Label className="text-xs uppercase text-muted-foreground tracking-wider font-semibold">Description</Label>
          {editMode === 'edit' ? (
            <Textarea value={draftDescription} onChange={(e) => setDraftDescription(e.target.value)} rows={3} className="mt-1" />
          ) : (
            <p className="text-sm mt-1">{testCase.description || <span className="text-muted-foreground italic">none</span>}</p>
          )}
        </div>

        {/* Steps — per-step structured shape after #596. Each step
            carries its own action prose + per-step expected outcomes;
            the legacy flat textareas (steps + expectedResult) are gone
            because the underlying schema doesn't carry them anymore. */}
        <div>
          <Label className="text-xs uppercase text-muted-foreground tracking-wider font-semibold">Steps</Label>
          {editMode === 'edit' ? (
            <div className="mt-1">
              <McpStepsEditor value={draftMcpSteps} onChange={setDraftMcpSteps} />
            </div>
          ) : (testCase.mcpSteps ?? []).length > 0 ? (
            <ol className="list-decimal list-inside space-y-2 mt-1">
              {testCase.mcpSteps.map((step, i) => (
                <li key={i} className="text-sm">
                  <span>{step.perform || <span className="text-muted-foreground italic">(no action)</span>}</span>
                  {step.expect.length > 0 && (
                    <ul className="list-none pl-5 mt-0.5 space-y-0.5">
                      {step.expect.map((e, ei) => (
                        <li key={ei} className="text-xs text-muted-foreground">
                          → Expect: {renderAssertion(e)}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-muted-foreground italic mt-1">none</p>
          )}
        </div>

        {/* Blocked reason */}
        {isBlocked && testCase.blockedReason && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">Blocked</h3>
            <p className="text-sm text-red-700 dark:text-red-300">{testCase.blockedReason}</p>
            {testCase.canRetrySignInBlock && (
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => void doRetry()}
                  disabled={retrying}
                  className="text-sm font-medium rounded-md border border-red-300 dark:border-red-700 bg-white dark:bg-transparent text-red-800 dark:text-red-200 px-3 py-1.5 hover:bg-red-100 dark:hover:bg-red-900 disabled:opacity-60"
                >
                  {retrying ? 'Checking…' : 'Try again'}
                </button>
                <span className="text-xs text-red-700/80 dark:text-red-300/80">
                  Checks the sign-in again. If it works now, this clears.
                </span>
              </div>
            )}
          </div>
        )}

        {/* Snapshots */}
        {testCase.snapshots && testCase.snapshots.length > 0 && (
          <div>
            <button
              onClick={() => setSnapshotsExpanded((v) => !v)}
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              {snapshotsExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Camera className="h-4 w-4" />
              Page Snapshots ({testCase.snapshots.length})
            </button>
            {snapshotsExpanded && (
              <div className="mt-2 space-y-2">
                {testCase.snapshots.map((snap, idx) => (
                  <div key={idx} className="border rounded-md">
                    <button
                      onClick={() => setExpandedSnapshotIndex(expandedSnapshotIndex === idx ? null : idx)}
                      className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/50"
                    >
                      <span className="text-xs">
                        <span className="font-medium">Step {snap.stepIndex}:</span>{' '}
                        <span className="text-muted-foreground">{snap.url}</span>
                      </span>
                      {expandedSnapshotIndex === idx ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    </button>
                    {expandedSnapshotIndex === idx && (
                      <pre className="p-3 text-xs font-mono bg-muted/30 overflow-x-auto max-h-64 overflow-y-auto border-t">
                        {snap.snapshot}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    )
  }

  function renderScriptPane() {
    // #2251 — no script row means the test hasn't been generated. Show the
    // "write this test first" placeholder in EVERY mode, not just Read, so the
    // script pane is never a writable empty editor whose typed content the save
    // would silently drop (the save omits scriptCode when there is no row).
    if (!merged?.scriptId) {
      return (
        <div className="h-full flex items-center justify-center p-6">
          <div className="text-center text-sm text-muted-foreground max-w-sm space-y-2">
            {testCase.status === 'planned' ? (
              <>
                <p>
                  This test has not been written yet.
                  {canWriteThisTest ? ' Use Write this test above.' : ' Resume Tests on the Runs page will write it.'}
                </p>
                {!canWriteThisTest && selectedVariant && (
                  <Button size="sm" variant="outline" onClick={() => navigate(wp('/results'))}>Go to Runs</Button>
                )}
              </>
            ) : isBlocked ? (
              <p>Script could not be generated — see the Blocked reason on the English pane.</p>
            ) : (
              <p>No script available.</p>
            )}
          </div>
        </div>
      )
    }
    return (
      <div className="h-full flex flex-col">
        {rewriteRequested && (
          <p className="flex-none border-b border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-700 dark:text-amber-400">
            Steps changed. This script will be rewritten on the next run that writes tests.
            {canWriteThisTest ? ' Use Write this Test above to do it now.' : ''}
          </p>
        )}
        <div className="flex-none px-3 py-1 border-b flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          <span>
            Playwright spec
            {merged?.scriptFilename && (
              <span className="ml-2 normal-case tracking-normal font-normal text-foreground/80">
                · {merged.scriptFilename}
              </span>
            )}
          </span>
          {editMode === 'read' && scriptCode && (
            <CopyIconButton value={scriptCode} title="Copy script" />
          )}
        </div>
        <div className="flex-1 min-h-0">
          {historyOpen && selectedHistorySha ? (
            revisionState.kind === 'loading' ? (
              <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                Loading revision…
              </div>
            ) : revisionState.kind === 'missing' ? (
              <div className="h-full flex items-center justify-center p-6 text-center text-xs text-muted-foreground">
                Revision not found in the per-app git repo (the history list may be stale).
              </div>
            ) : revisionState.kind === 'error' ? (
              <div className="h-full flex items-center justify-center p-6 text-center text-xs text-destructive" role="alert">
                Failed to load revision: {revisionState.message}
              </div>
            ) : revisionState.kind === 'ready' ? (
              <DiffPane
                // Current on the LEFT, selected revision on the RIGHT
                // — operators primarily want to answer "what would
                // change if I restored this?", which reads better when
                // their current code is the reference column. Monaco
                // colors invert accordingly (selected = +adds/-dels
                // against current as the baseline).
                original={scriptCode}
                modified={revisionState.revision.scriptCode ?? '// (script did not exist at this revision)\n'}
                originalLabel="HEAD · current"
                modifiedLabel={`${revisionState.revision.sha.slice(0, 7)} · selected`}
              />
            ) : null
          ) : (
            <MonacoCodePane
              value={editMode === 'edit' ? draftScript : scriptCode}
              onChange={(v) => editMode === 'edit' && setDraftScript(v)}
              readOnly={editMode === 'read'}
            />
          )}
        </div>
      </div>
    )
  }
}
